import { useCallback, useEffect, useRef, useState } from 'react';
import { uploadAttachment } from '@/api/attachments';
import { useLlmConfig } from '@/api/llmConfig';
import { currentUserKey } from '@/utils/session';
import {
  afterUpload,
  isInProgress,
  validateLocally,
  type UploadItem,
} from './attachmentState';

/**
 * Attachments chosen in the composer (button or drop) on their way to the
 * server, one request per file. Items of other Q&As keep uploading when
 * the user switches; each Q&A shows its own.
 */

const STORAGE_PREFIX = 'qa-assist.uploads.v1:';

interface Unfinished {
  id: string;
  name: string;
  size: number;
}

const storageKey = (inquiryId: string) =>
  `${STORAGE_PREFIX}${currentUserKey() ?? 'anonymous'}:${inquiryId}`;

const readUnfinished = (inquiryId: string): Unfinished[] => {
  try {
    const raw = localStorage.getItem(storageKey(inquiryId));
    const list = raw ? (JSON.parse(raw) as Unfinished[]) : [];
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
};

const writeUnfinished = (inquiryId: string, list: Unfinished[]) => {
  try {
    if (list.length) {
      localStorage.setItem(storageKey(inquiryId), JSON.stringify(list));
    } else {
      localStorage.removeItem(storageKey(inquiryId));
    }
  } catch {
    // Not kept across a reload; nothing else depends on it.
  }
};

/**
 * Set while the page unloads. Firefox and Safari abort the uploads of a
 * page being left and still run their error handlers, before pagehide:
 * those uploads did not fail, they are offered for reattachment after the
 * reload. An unload can be cancelled (the documents upload dialog asks), so
 * the flag clears itself after a second; a page that really goes away never
 * gets that far.
 */
let unloading = false;
if (typeof window !== 'undefined') {
  let reset: ReturnType<typeof setTimeout> | undefined;
  const mark = () => {
    unloading = true;
    clearTimeout(reset);
    reset = setTimeout(() => {
      unloading = false;
    }, 1000);
  };
  window.addEventListener('beforeunload', mark);
  window.addEventListener('pagehide', mark);
}

let sequence = 0;
const localId = () => `u${Date.now().toString(36)}${(sequence += 1)}`;

const blankItem = (file: { name: string; size: number }): UploadItem => ({
  id: localId(),
  name: file.name,
  size: file.size,
  state: 'validating',
  percent: 0,
  message: null,
  reasonKey: null,
  retryable: false,
  fileId: null,
});

export const useAttachmentUploads = ({
  inquiryId,
  serverNames,
  serverFileIds,
  serverReady,
  onUploaded,
}: {
  inquiryId: string | undefined;
  /** Names of the Q&A's attachments the server already has. */
  serverNames: string[];
  /** Ids of those attachments. */
  serverFileIds: string[];
  /** The server's list of this Q&A's attachments has loaded. */
  serverReady: boolean;
  /** A file reached the server: reload the Q&A's attachments and totals. */
  onUploaded: (inquiryId: string) => void;
}) => {
  const config = useLlmConfig();
  const [byInquiry, setByInquiry] = useState<Record<string, UploadItem[]>>({});
  const files = useRef(new Map<string, File>());
  const controllers = useRef(new Map<string, AbortController>());
  const onUploadedRef = useRef(onUploaded);
  onUploadedRef.current = onUploaded;
  const restored = useRef(new Set<string>());

  const update = useCallback(
    (inquiry: string, id: string, change: (item: UploadItem) => UploadItem) =>
      setByInquiry(previous => ({
        ...previous,
        [inquiry]: (previous[inquiry] ?? []).map(item =>
          item.id === id ? change(item) : item,
        ),
      })),
    [],
  );

  const remember = (inquiry: string, item: UploadItem, keep: boolean) => {
    const list = readUnfinished(inquiry).filter(entry => entry.id !== item.id);
    if (keep) list.push({ id: item.id, name: item.name, size: item.size });
    writeUnfinished(inquiry, list);
  };

  const start = useCallback(
    async (inquiry: string, item: UploadItem) => {
      const file = files.current.get(item.id);
      if (!file) return;
      const controller = new AbortController();
      controllers.current.set(item.id, controller);
      remember(inquiry, item, true);
      update(inquiry, item.id, current => ({
        ...current,
        state: 'uploading',
        percent: 0,
        message: null,
        reasonKey: null,
      }));
      const result = await uploadAttachment(inquiry, file, {
        signal: controller.signal,
        onProgress: percent =>
          update(inquiry, item.id, current =>
            current.state === 'uploading'
              ? { ...current, percent: Math.min(percent, 99) }
              : current,
          ),
      });
      controllers.current.delete(item.id);
      if (unloading && result.kind !== 'done') return;
      remember(inquiry, item, false);
      update(inquiry, item.id, current => afterUpload(current, result));
      if (result.kind === 'done' && result.outcome.status === 'accepted') {
        files.current.delete(item.id);
        onUploadedRef.current(inquiry);
      }
    },
    [update],
  );

  /** Attach files to the open Q&A. */
  const add = useCallback(
    (list: FileList | File[]) => {
      const inquiry = inquiryId;
      if (!inquiry) return;
      const chosen = Array.from(list);
      const items = chosen.map(blankItem);
      chosen.forEach((file, index) => files.current.set(items[index].id, file));
      setByInquiry(previous => ({
        ...previous,
        [inquiry]: [...(previous[inquiry] ?? []), ...items],
      }));
      // Validating is visible for a moment before each upload starts.
      setTimeout(() => {
        items.forEach((item, index) => {
          const problem = validateLocally(chosen[index], config);
          if (problem) {
            files.current.delete(item.id);
            update(inquiry, item.id, current => ({
              ...current,
              ...problem,
              retryable: false,
            }));
            return;
          }
          void start(inquiry, item);
        });
      }, 0);
    },
    [config, inquiryId, start, update],
  );

  const cancel = useCallback((id: string) => {
    controllers.current.get(id)?.abort();
  }, []);

  const retry = useCallback(
    (id: string) => {
      const inquiry = inquiryId;
      const item = inquiry
        ? byInquiry[inquiry]?.find(entry => entry.id === id)
        : undefined;
      if (!inquiry || !item || !files.current.has(id)) return;
      void start(inquiry, item);
    },
    [byInquiry, inquiryId, start],
  );

  const remove = useCallback(
    (id: string) => {
      const inquiry = inquiryId;
      if (!inquiry) return;
      controllers.current.get(id)?.abort();
      files.current.delete(id);
      writeUnfinished(
        inquiry,
        readUnfinished(inquiry).filter(entry => entry.id !== id),
      );
      setByInquiry(previous => ({
        ...previous,
        [inquiry]: (previous[inquiry] ?? []).filter(item => item.id !== id),
      }));
    },
    [inquiryId],
  );

  // After a reload, uploads that were on their way need the file again,
  // unless the server got them meanwhile.
  const serverKey = serverNames.join('\u0000');
  useEffect(() => {
    if (!inquiryId || !serverReady) return;
    // A reattach item the server turns out to have is not missing.
    setByInquiry(previous => {
      const list = previous[inquiryId];
      if (!list?.some(item => item.state === 'reattach')) return previous;
      return {
        ...previous,
        [inquiryId]: list.filter(
          item => item.state !== 'reattach' || !serverNames.includes(item.name),
        ),
      };
    });
    if (restored.current.has(inquiryId)) return;
    restored.current.add(inquiryId);
    const leftovers = readUnfinished(inquiryId).filter(
      entry =>
        !files.current.has(entry.id) && !serverNames.includes(entry.name),
    );
    writeUnfinished(inquiryId, leftovers);
    if (leftovers.length === 0) return;
    setByInquiry(previous => ({
      ...previous,
      [inquiryId]: [
        ...(previous[inquiryId] ?? []),
        ...leftovers.map(entry => ({
          ...blankItem(entry),
          id: entry.id,
          state: 'reattach' as const,
        })),
      ],
    }));
    // serverKey stands for serverNames.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inquiryId, serverKey, serverReady]);

  // A finished upload hands over to the server's row once it is listed;
  // afterwards removing the attachment cannot bring the upload row back.
  const listedKey = serverFileIds.join(',');
  useEffect(() => {
    if (!inquiryId || serverFileIds.length === 0) return;
    setByInquiry(previous => {
      const list = previous[inquiryId];
      const kept = list?.filter(
        item =>
          !(
            item.state === 'complete' &&
            serverFileIds.includes(String(item.fileId))
          ),
      );
      return kept && kept.length !== list.length
        ? { ...previous, [inquiryId]: kept }
        : previous;
    });
    // listedKey stands for serverFileIds.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inquiryId, listedKey]);

  const items = inquiryId ? (byInquiry[inquiryId] ?? []) : [];
  return {
    items,
    uploading: items.filter(isInProgress).length,
    add,
    cancel,
    retry,
    remove,
    canRetry: (item: UploadItem) =>
      item.retryable && files.current.has(item.id),
  };
};
